import {
	TEMPLATE_PICKER_QUERY_PARAM,
	INSERT_TEMPLATE_WITH_KEY_QUERY_PARAM,
	HEADLESS_QUERY_PARAM,
	RELAY_SPOT_PARAM,
	SERVER_SPOT_PARAM,
	WHITEBOARD_LOCALE,
	INITIAL_THEME_PARAM,
	WHITEBOARD_LINKED_OBJECT,
	JIRA_IMPORT_TOOL_QUERY_PARAM,
	CLOUD_ID,
	PROJECT_UUID,
	PARENT_PRODUCT_QUERY_PARAM,
	PARENT_DOMAIN_QUERY_PARAM,
	EMBEDDED_WHITEBOARD_CREATED_AT_QUERY_PARAM,
	FOCUSED_COMMENT_ID,
	REPLY_TO_COMMENT,
	WHITEBOARD_VERSION_ID,
	CREATED_FROM_SLUG,
	BIFROST_OVERRIDE_PARAM,
	VISUAL_REFRESH_PARAM,
	MIRO_IMPORT_TOOL_QUERY_PARAM,
} from '@confluence/whiteboard-utils';

export const getWhiteboardURLBase = (parentProduct?: string | null) => {
	// When parent product is provided, the base canvas html need to load from canvas-server
	// to get the correct CSP header
	return parentProduct ? '/canvas-embed' : '/whiteboards';
};

interface WhiteboardURLOptions {
	openTemplatePicker?: boolean;
	insertTemplateWithKey?: string;
	headless?: boolean;
	canvasServerSpot?: number;
	canvasRelaySpot?: number;
	whiteboardLocale?: string;
	whiteboardLinkedObject?: string;
	initialTheme?: string; // serialized theme state
	openJiraImportTool?: boolean;
	cloudId?: string;
	projectUuid?: string;
	parentProduct?: string;
	parentDomain?: string;
	/**
	 * Only used for embedded Whiteboards to show creation time
	 * @see EMBEDDED_WHITEBOARD_CREATED_AT_QUERY_PARAM
	 */
	createdAt?: string;
	focusedCommentId?: string;
	replyToComment?: string;
	whiteboardVersionId?: string | null;
	createdFromSlug?: string;
	bifrostVersion?: string;
	visualRefresh?: boolean;
	openMiroImportTool?: boolean;
}

const queryParams = (params: URLSearchParams) => {
	const paramsString = `${params}`;
	return paramsString ? `?${paramsString}` : '';
};

export const getWhiteboardURL = (
	referenceId?: string | null,
	{
		openTemplatePicker,
		insertTemplateWithKey,
		headless,
		canvasRelaySpot,
		canvasServerSpot,
		whiteboardLocale,
		whiteboardLinkedObject,
		initialTheme,
		openJiraImportTool,
		cloudId,
		projectUuid,
		parentProduct,
		parentDomain,
		createdAt,
		focusedCommentId,
		replyToComment,
		whiteboardVersionId,
		createdFromSlug,
		bifrostVersion,
		visualRefresh,
		openMiroImportTool,
	}: WhiteboardURLOptions = {},
) => {
	const rawInputParams = {
		[HEADLESS_QUERY_PARAM]: headless,
		[SERVER_SPOT_PARAM]: canvasServerSpot,
		[RELAY_SPOT_PARAM]: canvasRelaySpot,
		[WHITEBOARD_LOCALE]: whiteboardLocale,
		[WHITEBOARD_LINKED_OBJECT]: whiteboardLinkedObject,
		[INITIAL_THEME_PARAM]: initialTheme,
		[PARENT_PRODUCT_QUERY_PARAM]: parentProduct,
		[PARENT_DOMAIN_QUERY_PARAM]: parentDomain,
		[EMBEDDED_WHITEBOARD_CREATED_AT_QUERY_PARAM]: createdAt,
		[FOCUSED_COMMENT_ID]: focusedCommentId,
		[REPLY_TO_COMMENT]: replyToComment,
	};

	const inputParams = Object.entries(rawInputParams)
		.filter(([, v]) => !!v)
		.map(([k, v]) => [k, String(v)]);

	const params = new URLSearchParams(inputParams);

	if (bifrostVersion) {
		params.set(BIFROST_OVERRIDE_PARAM, bifrostVersion);
	}

	if (!referenceId) {
		return `/whiteboard/loading${queryParams(params)}`;
	}

	openTemplatePicker !== undefined &&
		params.set(TEMPLATE_PICKER_QUERY_PARAM, openTemplatePicker ? 'true' : 'false');
	openJiraImportTool !== undefined &&
		params.set(JIRA_IMPORT_TOOL_QUERY_PARAM, openJiraImportTool ? 'true' : 'false');
	openMiroImportTool !== undefined &&
		params.set(MIRO_IMPORT_TOOL_QUERY_PARAM, openMiroImportTool ? 'true' : 'false');
	cloudId && params.set(CLOUD_ID, cloudId);
	projectUuid && params.set(PROJECT_UUID, projectUuid);
	insertTemplateWithKey && params.set(INSERT_TEMPLATE_WITH_KEY_QUERY_PARAM, insertTemplateWithKey);
	whiteboardVersionId && params.set(WHITEBOARD_VERSION_ID, whiteboardVersionId);

	if (createdFromSlug) {
		params.set(CREATED_FROM_SLUG, createdFromSlug);
	}

	if (visualRefresh) {
		params.set(VISUAL_REFRESH_PARAM, visualRefresh ? 'true' : 'false');
	}

	return `/whiteboard/${referenceId}${queryParams(params)}`;
};
